import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { formatDistanceToNow, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Inbox, X, CalendarDays, Plane, Laptop, Timer, ClipboardCheck } from "lucide-react";

const KIND = {
  ferie: { icon: CalendarDays, color: "#0d6cb8" },
  trasferta: { icon: Plane, color: "#7c3aed" },
  smartworking: { icon: Laptop, color: "#059669" },
  straordinario: { icon: Timer, color: "#ea580c" },
};

const statusCategory = (it) => {
  if (it.pending) return "pending";
  if (it.status === "approvata") return "approved";
  if (it.status === "rifiutata") return "rejected";
  return "other";
};

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  other: "bg-gray-100 text-gray-500",
};

const SEEN_KEY = "tb_inbox_seen_at";

// Centro notifiche a scomparsa: pannello laterale con lo storico delle ultime
// richieste di approvazione ricevute (orario + mittente). Visibile ai responsabili/admin.
const ApprovalsInboxPanel = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [highlightSince, setHighlightSince] = useState(null);
  const seenRef = useRef(localStorage.getItem(SEEN_KEY) || "");

  const recomputeNew = useCallback((list) => {
    const seen = seenRef.current;
    const n = list.filter((x) => (x.created_at || "") > seen).length;
    setNewCount(n);
  }, []);

  const load = useCallback(() => {
    api.get("/approvals/recent?limit=30")
      .then((r) => {
        const list = r.data?.items || [];
        setItems(list);
        recomputeNew(list);
      })
      .catch(() => {});
  }, [recomputeNew]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onChanged = () => load();
    window.addEventListener("tb-approvals-changed", onChanged);
    return () => { clearInterval(t); window.removeEventListener("tb-approvals-changed", onChanged); };
  }, [load]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setHighlightSince(seenRef.current);
      load();
      const now = new Date().toISOString();
      seenRef.current = now;
      localStorage.setItem(SEEN_KEY, now);
      setNewCount(0);
    }
  };

  const ago = (iso) => {
    try { return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: it }); }
    catch { return ""; }
  };

  const goAll = () => { setOpen(false); navigate("/approvazioni"); };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        data-testid="approvals-inbox-btn"
        title="Richieste ricevute"
        className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-600"
      >
        <Inbox size={19} />
        {newCount > 0 && (
          <span data-testid="approvals-inbox-dot" className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 bg-[#ff7e00] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {newCount > 9 ? "9+" : newCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60] animate-in fade-in duration-200" onClick={() => setOpen(false)} data-testid="approvals-inbox-backdrop" />
          <div className="fixed right-0 top-0 h-full w-[380px] max-w-[92vw] bg-white shadow-2xl z-[61] flex flex-col animate-in slide-in-from-right duration-300" data-testid="approvals-inbox-panel">
            <div className="flex items-center justify-between px-4 h-16 bg-[#16345c] shrink-0">
              <span className="flex items-center gap-2 text-white font-bold text-[15px]">
                <ClipboardCheck size={18} /> Richieste ricevute
              </span>
              <button onClick={() => setOpen(false)} data-testid="approvals-inbox-close" className="text-white/80 hover:text-white transition-colors" aria-label="Chiudi">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="approvals-inbox-list">
              {items.length === 0 ? (
                <div className="p-10 text-center text-gray-400 text-[13px]" data-testid="approvals-inbox-empty">
                  <Inbox size={30} className="mx-auto mb-2 opacity-40" />
                  Nessuna richiesta ricevuta
                </div>
              ) : (
                items.map((it) => {
                  const meta = KIND[it.kind] || KIND.ferie;
                  const Icon = meta.icon;
                  const cat = statusCategory(it);
                  const isNew = highlightSince !== null && (it.created_at || "") > highlightSince;
                  return (
                    <button
                      key={`${it.kind}-${it.id}`}
                      onClick={goAll}
                      data-testid={`approvals-inbox-item-${it.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 flex items-start gap-3 transition-colors ${isNew ? "bg-orange-50 hover:bg-orange-100" : "hover:bg-gray-50"}`}
                    >
                      <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
                        <Icon size={16} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-bold text-[#16345c] truncate">{it.employee_name}</span>
                          {it.matricola && <span className="text-[11px] text-gray-400">({it.matricola})</span>}
                          {isNew && <span className="text-[9px] font-black uppercase tracking-wide bg-[#ff7e00] text-white px-1.5 py-0.5 rounded-full">Nuovo</span>}
                        </div>
                        <div className="text-[12.5px] text-gray-700 font-semibold leading-snug">{it.title}</div>
                        <div className="text-[12px] text-gray-500 leading-snug">{it.detail}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${STATUS_CLS[cat]}`}>{it.status_label}</span>
                          <span className="text-[11px] text-gray-400">{ago(it.created_at)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className="shrink-0 border-t border-gray-200 p-3">
              <button onClick={goAll} data-testid="approvals-inbox-open-all" className="w-full flex items-center justify-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold py-2.5 rounded-md transition-colors">
                <ClipboardCheck size={15} /> Vai alle approvazioni
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApprovalsInboxPanel;
